import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, Modal, ModalContent, ModalFooter, Select, SelectOption } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    ChangeMembershipRoleDocument,
    PortalUserDocument,
    RoleSummaryFragment,
} from '../../graphql/__generated__/types';
import { changeRoleRefusalOf } from './changeRoleRefusal';
import { findRole, refusalRules, roleRefusal } from './onboarding/grantableRoles';

interface Props {
    membershipId: string;
    organizationName: string;
    personName: string;
    currentRoleId: string | null;
    currentRoleName: string;
    roles: RoleSummaryFragment[];
    onClose: () => void;
}

const ChangeRoleDialog = ({
    membershipId,
    organizationName,
    personName,
    currentRoleId,
    currentRoleName,
    roles,
    onClose,
}: Props) => {
    const [selectedId, setSelectedId] = useState<string | null>(currentRoleId);

    const [changeRole, { loading, error }] = useMutation(ChangeMembershipRoleDocument, {
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const options: SelectOption<string>[] = roles
        .filter((role) => role.id)
        .map((role) => ({
            label: role.name ?? '',
            value: role.id as string,
            disabled: !role.grantableByViewer,
            disabledReason: role.grantableByViewer ? undefined : roleRefusal(role.notGrantableReason).tag,
        }));

    const rules = refusalRules(roles);
    const chosenRefused = selectedId !== currentRoleId && findRole(roles, selectedId)?.grantableByViewer === false;

    const submit = () => {
        // Catch the rejection: the refusal is read off `error`, not thrown to the console.
        changeRole({ variables: { membershipId, roleId: selectedId as string } })
            .then((result) => {
                if (result.data) {
                    onClose();
                }
            })
            .catch(() => {});
    };

    return (
        <Modal open onClose={onClose} title={c('Change role').t`Change the role`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{personName}</b>
                        <span className="text-text-600"> · {organizationName}</span>
                    </p>
                    <p className="text-sm text-text-600">
                        {c('Change role').t`Current role:`} <b className="text-text-900">{currentRoleName}</b>
                    </p>

                    <div>
                        <label htmlFor="change-role-select" className="mb-1.5 block text-sm font-medium text-text-900">
                            {c('Change role').t`New role`}
                        </label>
                        <Select
                            id="change-role-select"
                            options={options}
                            value={options.find((option) => option.value === selectedId)}
                            onChange={(option) => setSelectedId(option.value)}
                            placeholder={c('Change role').t`Pick a role`}
                            emptyLabel={c('Change role').t`The roles have not loaded. Reopen this sheet.`}
                        />
                        {rules.map((rule) => (
                            <p key={rule} className="mt-1.5 text-xs text-text-600">
                                {rule}
                            </p>
                        ))}
                    </div>

                    {error && <RefusalNotice refusal={changeRoleRefusalOf(error)} />}
                </div>
            </ModalContent>

            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button
                    category="default"
                    loading={loading}
                    disabled={!selectedId || selectedId === currentRoleId || chosenRefused}
                    title={chosenRefused ? roleRefusal(findRole(roles, selectedId)?.notGrantableReason).tag : undefined}
                    onClick={submit}
                >
                    {c('Action').t`Save`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChangeRoleDialog;
